import React, {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

// delay is equal to CSS transition timing
const TRANSITION_DELAY = 175;

function getDimensions(element) {
  const { height, width } = element.getBoundingClientRect();
  return { height: Math.round(height), width: Math.round(width) };
}

const ZoomablePhoto = ({ source, zoom, isFullscreen }) => {
  const scale = useRef(1);
  const containerDimensions = useRef({ height: 0, width: 0 });
  const imageDimensions = useRef({ height: 0, width: 0 });
  const previousCoords = useRef({ x: null, y: null });
  const offsetCoords = useRef({ x: null, y: null });
  const container = useRef();
  const image = useRef();

  const [mouseDown, setMouseDown] = useState(false);

  const updateContainerDimensions = useCallback(() => {
    setTimeout(() => {
      const dimensions = getDimensions(container.current);
      containerDimensions.current = dimensions;
    }, TRANSITION_DELAY);
  }, []);

  const updatePosition = (offsetX, offsetY) => {
    let x = offsetX;
    let y = offsetY;
    if (typeof offsetX === 'number') {
      x = `${x}px`;
    }

    if (typeof offsetY === 'number') {
      y = `${y}px`;
    }

    image.current.style.top = y;
    image.current.style.left = x;
  };

  useEffect(() => {
    window.addEventListener('resize', updateContainerDimensions);

    updateContainerDimensions();
    return () => {
      window.removeEventListener('resize', updateContainerDimensions);
    };
  }, [updateContainerDimensions]);

  useLayoutEffect(() => {
    imageDimensions.current = getDimensions(image.current);

    if (zoom > 1) {
      const x =
        (containerDimensions.current.width - imageDimensions.current.width) / 2;
      const y =
        (containerDimensions.current.height - imageDimensions.current.height) /
        2;

      offsetCoords.current = { x, y };

      updatePosition(x, y);
    } else {
      offsetCoords.current = { x: null, y: null };
      previousCoords.current = { x: null, y: null };

      updatePosition('auto', 'auto');
    }
  }, [zoom]);

  useEffect(() => {
    updateContainerDimensions();
  }, [isFullscreen, updateContainerDimensions]);

  const imageLoaded = () => {
    imageDimensions.current = getDimensions(image.current);
    scale.current = image.current.height / image.current.width;

    updatePosition('auto', 'auto');
  };

  const handleMouseMoved = (e) => {
    if (zoom === 1 || !mouseDown) {
      return;
    }

    const { width, height } = getDimensions(image.current);
    const { width: containerWidth, height: containerHeight } =
      containerDimensions.current;

    let nextY = offsetCoords.current.y + e.clientY - previousCoords.current.y;
    let nextX = offsetCoords.current.x + e.clientX - previousCoords.current.x;

    const minX = width < containerWidth ? (containerWidth - width) * 0.5 : 0;
    const minY =
      height < containerHeight ? (containerHeight - height) * 0.5 : 0;

    nextY = Math.min(minY, Math.max(nextY, -1 * (height - containerHeight)));
    nextX = Math.min(minX, Math.max(nextX, -1 * (width - containerWidth)));

    offsetCoords.current = { x: nextX, y: nextY };
    previousCoords.current = { x: e.clientX, y: e.clientY };

    updatePosition(nextX, nextY);
  };

  const containerClasses = cx('asset-container', {
    'full-width': isFullscreen,
    'zoom1-5x': zoom === 1.5,
    zoom2x: zoom === 2,
    'zoom2-5x': zoom === 2.5,
    zoom3x: zoom === 3,
    'zoom3-5x': zoom === 3.5,
    zoom4x: zoom === 4,
  });

  return (
    <div
      ref={container}
      className={containerClasses}
      onMouseDown={(e) => {
        previousCoords.current = { x: e.clientX, y: e.clientY };
        setMouseDown(true);
      }}
      onMouseLeave={() => {
        setMouseDown(false);
      }}
      onMouseUp={() => {
        setMouseDown(false);
      }}
      onMouseMove={handleMouseMoved}
    >
      <img
        ref={image}
        draggable={zoom === 1}
        onDragStart={(e) => {
          if (zoom > 1) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }

          return true;
        }}
        alt=""
        src={source}
        onLoad={imageLoaded}
      />
    </div>
  );
};

ZoomablePhoto.propTypes = {
  zoom: PropTypes.number,
  isFullscreen: PropTypes.bool,
  source: PropTypes.string,
};

export default ZoomablePhoto;
